/**
 * Hook useInactivityAlert
 * Detecta inatividade do usuário e exibe alertas
 * 
 * @author Equipe GestaoeCRM
 * @date 02/10/2025
 * @version 1.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { workSessionKeys } from '../services/workSessionApi';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export interface InactivityConfig {
  /** Habilitar o hook (padrão: true) */
  enabled?: boolean;
  /** Tempo em minutos para considerar inatividade (padrão: 15) */
  inactivityThreshold?: number;
  /** Tempo em minutos para alerta de inatividade (padrão: 10) */
  warningThreshold?: number;
  /** Tempo em minutos para pausa automática (padrão: 30) */
  autoPauseThreshold?: number;
  /** Habilitar pausa automática (padrão: false) */
  enableAutoPause?: boolean;
  /** Habilitar notificações do navegador (padrão: true) */
  enableBrowserNotifications?: boolean;
  /** Eventos a serem monitorados */
  events?: string[];
}

export interface InactivityState {
  /** Se o usuário está inativo */
  isInactive: boolean;
  /** Tempo de inatividade em minutos */
  inactiveTime: number;
  /** Se deve mostrar alerta de inatividade */
  showWarning: boolean;
  /** Se deve pausar automaticamente */
  shouldAutoPause: boolean;
  /** Última atividade detectada */
  lastActivity: Date;
  /** Se há sessões ativas */
  hasActiveSessions: boolean;
}

export interface InactivityActions {
  /** Resetar timer de inatividade */
  resetInactivity: () => void;
  /** Marcar como ativo manualmente */
  markAsActive: () => void;
  /** Pausar sessões ativas */
  pauseActiveSessions: () => Promise<void>;
  /** Retomar sessões pausadas */
  resumeActiveSessions: () => Promise<void>;
  /** Solicitar permissão para notificações */
  requestNotificationPermission: () => Promise<boolean>;
}

// ============================================================================
// CONFIGURAÇÕES PADRÃO
// ============================================================================

const DEFAULT_CONFIG: Required<InactivityConfig> = {
  enabled: true,
  inactivityThreshold: 15, // 15 minutos
  warningThreshold: 10, // 10 minutos
  autoPauseThreshold: 30, // 30 minutos
  enableAutoPause: false,
  enableBrowserNotifications: true,
  events: [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'focus',
    'blur'
  ]
};

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export const useInactivityAlert = (
  config: InactivityConfig = {}
): [InactivityState, InactivityActions] => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  const { enabled } = finalConfig;
  
  // Estados
  const [lastActivity, setLastActivity] = useState<Date>(new Date());
  const [isInactive, setIsInactive] = useState(false);
  const [inactiveTime, setInactiveTime] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [shouldAutoPause, setShouldAutoPause] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  
  // Refs
  const inactivityTimerRef = useRef<NodeJS.Timeout>();
  const warningShownRef = useRef(false);
  const autoPauseExecutedRef = useRef(false);
  
  // Query para buscar sessões ativas
  const { data: activeSessions = [] } = useQuery({
    queryKey: workSessionKeys.active(),
    queryFn: async () => {
      // Importar dinamicamente para evitar circular dependency
      const { listActiveSessions } = await import('../services/workSessionApi');
      return listActiveSessions();
    },
    refetchInterval: 30000, // Verificar a cada 30 segundos
    enabled: enabled, // Usar a condição enabled do config
  });
  
  const hasActiveSessions = Array.isArray(activeSessions) ? activeSessions.length > 0 : false;
  
  // ============================================================================
  // FUNÇÕES AUXILIARES
  // ============================================================================
  
  /** Solicitar permissão para notificações */
  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      // console.warn('Este navegador não suporta notificações');
      return false;
    }
    
    if (Notification.permission === 'granted') {
      return true;
    }
    
    if (Notification.permission === 'denied') {
      return false;
    }
    
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    return permission === 'granted';
  }, []);
  
  /** Enviar notificação do navegador */
  const sendBrowserNotification = useCallback((title: string, body: string, icon?: string) => {
    if (!finalConfig.enableBrowserNotifications || notificationPermission !== 'granted') {
      return;
    }
    
    const notification = new Notification(title, {
      body,
      icon: icon || '/favicon.ico',
      tag: 'inactivity-alert',
      requireInteraction: true,
    });
    
    // Auto-fechar após 10 segundos
    setTimeout(() => {
      notification.close();
    }, 10000);
    
    return notification;
  }, [finalConfig.enableBrowserNotifications, notificationPermission]);
  
  /** Pausar todas as sessões ativas */
  const pauseActiveSessions = useCallback(async (): Promise<void> => {
    if (!hasActiveSessions) return;
    
    try {
      // Importar dinamicamente para evitar circular dependency
      const { pauseSession } = await import('../services/workSessionApi');
      
      const pausePromises = Array.isArray(activeSessions) 
        ? activeSessions
            .filter(session => !session.isPaused)
            .map(session => pauseSession(session.orderOfServiceId, session.id))
        : [];
      
      await Promise.all(pausePromises);
      
      // Notificar usuário
      sendBrowserNotification(
        'Sessões Pausadas por Inatividade',
        `${pausePromises.length} sessão(ões) foram pausadas automaticamente devido à inatividade.`
      );
      
      // console.log(`Pausadas ${pausePromises.length} sessões por inatividade`);
    } catch (error) {
      // console.error('Erro ao pausar sessões por inatividade:', error);
    }
  }, [activeSessions, hasActiveSessions, sendBrowserNotification]);
  
  /** Retomar sessões pausadas */
  const resumeActiveSessions = useCallback(async (): Promise<void> => {
    if (!hasActiveSessions) return;
    
    try {
      // Importar dinamicamente para evitar circular dependency
      const { resumeSession } = await import('../services/workSessionApi');
      
      const resumePromises = Array.isArray(activeSessions)
        ? activeSessions
            .filter(session => session.isPaused)
            .map(session => resumeSession(session.orderOfServiceId, session.id))
        : [];
      
      await Promise.all(resumePromises);
      
      // console.log(`Retomadas ${resumePromises.length} sessões`);
    } catch (error) {
      // console.error('Erro ao retomar sessões:', error);
    }
  }, [activeSessions, hasActiveSessions]);
  
  /** Resetar timer de inatividade */
  const resetInactivity = useCallback(() => {
    const now = new Date();
    setLastActivity(now);
    setIsInactive(false);
    setInactiveTime(0);
    setShowWarning(false);
    setShouldAutoPause(false);
    
    // Resetar flags
    warningShownRef.current = false;
    autoPauseExecutedRef.current = false;
    
    // Limpar timer existente
    if (inactivityTimerRef.current) {
      clearInterval(inactivityTimerRef.current);
    }
    
    // Iniciar novo timer apenas se há sessões ativas
    if (hasActiveSessions) {
      inactivityTimerRef.current = setInterval(() => {
        const timeDiff = Date.now() - now.getTime();
        const minutesInactive = Math.floor(timeDiff / (1000 * 60));
        
        setInactiveTime(minutesInactive);
        
        // Verificar se deve mostrar alerta
        if (minutesInactive >= finalConfig.warningThreshold && !warningShownRef.current) {
          setShowWarning(true);
          warningShownRef.current = true;
          
          sendBrowserNotification(
            'Inatividade Detectada',
            `Você está inativo há ${minutesInactive} minutos. Suas sessões de trabalho ainda estão ativas.`
          );
        }
        
        // Verificar se deve marcar como inativo
        if (minutesInactive >= finalConfig.inactivityThreshold) {
          setIsInactive(true);
        }
        
        // Verificar se deve pausar automaticamente
        if (
          finalConfig.enableAutoPause &&
          minutesInactive >= finalConfig.autoPauseThreshold &&
          !autoPauseExecutedRef.current
        ) {
          setShouldAutoPause(true);
          autoPauseExecutedRef.current = true;
          
          // Executar pausa automática
          pauseActiveSessions();
        }
      }, 60000); // Verificar a cada minuto
    }
  }, [
    hasActiveSessions,
    finalConfig.warningThreshold,
    finalConfig.inactivityThreshold,
    finalConfig.autoPauseThreshold,
    finalConfig.enableAutoPause,
    sendBrowserNotification,
    pauseActiveSessions
  ]);
  
  /** Marcar como ativo manualmente */
  const markAsActive = useCallback(() => {
    resetInactivity();
  }, [resetInactivity]);
  
  // ============================================================================
  // EFEITOS
  // ============================================================================
  
  // Configurar listeners de eventos
  useEffect(() => {
    const handleActivity = () => {
      resetInactivity();
    };
    
    // Adicionar listeners apenas se há sessões ativas
    if (hasActiveSessions) {
      finalConfig.events.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });
      
      // Inicializar timer
      resetInactivity();
    }
    
    return () => {
      // Remover listeners
      finalConfig.events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      // Limpar timer
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
    };
  }, [hasActiveSessions, finalConfig.events, resetInactivity]);
  
  // Verificar permissão de notificação no mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);
  
  // Limpar timer quando não há sessões ativas
  useEffect(() => {
    if (!hasActiveSessions) {
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
      }
      
      setIsInactive(false);
      setInactiveTime(0);
      setShowWarning(false);
      setShouldAutoPause(false);
      warningShownRef.current = false;
      autoPauseExecutedRef.current = false;
    }
  }, [hasActiveSessions]);
  
  // ============================================================================
  // RETORNO
  // ============================================================================
  
  const state: InactivityState = {
    isInactive,
    inactiveTime,
    showWarning,
    shouldAutoPause,
    lastActivity,
    hasActiveSessions,
  };
  
  const actions: InactivityActions = {
    resetInactivity,
    markAsActive,
    pauseActiveSessions,
    resumeActiveSessions,
    requestNotificationPermission,
  };
  
  return [state, actions];
};

export default useInactivityAlert;